import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import { getRegionFromZipCode, REGIONAL_CLIMATE_DATA } from "@/data/regional-climate"
import { addDays, parseFrostDate } from "./date-utils"
import {
  attachToolEngineMetadata,
  buildCanonicalToolContext,
  type DeterministicToolResultMetadata,
} from "@/lib/lawn-plan-engine"

/**
 * User-estimated thatch depth. Thatch is the spongy brown layer of living and
 * dead stems/roots between the green blades and the soil. Homeowners measure it
 * by cutting a small wedge of turf and eyeballing the brown layer, so we take a
 * band rather than a precise number.
 */
export type ThatchDepthBand = "under-quarter" | "quarter-to-half" | "half-to-one" | "over-one"

export type DethatchVerdict = "monitor" | "dethatch" | "dethatch-urgent"

export type DethatchMethod = "none" | "manual-rake" | "power-rake" | "vertical-mower"

export type DethatchStatus = "optimal" | "acceptable" | "approaching" | "wait" | "off-season"

export interface DethatchWindow {
  start: Date
  end: Date
  isPrimary: boolean
  description: string
}

export interface DethatchingResult extends DeterministicToolResultMetadata {
  grassType: string
  zipCode: string
  region: string
  season: "Warm" | "Cool"
  currentSeason: "spring" | "summer" | "fall" | "winter"

  // Thatch assessment
  thatchDepth: ThatchDepthBand
  thatchDepthLabel: string
  thatchInches: string

  // Verdict
  verdict: DethatchVerdict
  needsDethatching: boolean
  verdictHeadline: string
  verdictSummary: string

  // Method + equipment
  method: DethatchMethod
  methodLabel: string
  methodReason: string
  passes: string
  equipmentGuidance: string

  // Timing
  primaryWindow: DethatchWindow
  secondaryWindow: DethatchWindow | null
  recommendedWindow: DethatchWindow
  status: DethatchStatus
  timingAdvice: string
  timeUntilOptimal: string | null
  daysRemaining?: number

  regionalData: {
    region: string
    lastFrost: Date | null
    firstFrost: Date | null
  }

  steps: string[]
  followUpActions: string[]
  avoidActions: string[]

  lawnSizeSqft: number | null
}

export interface DethatchingInput {
  grassType: string
  zipCode: string
  thatchDepth: ThatchDepthBand
  lawnSize?: string | number | null
}

const THATCH_LABELS: Record<ThatchDepthBand, { label: string; inches: string }> = {
  "under-quarter": { label: "Less than 1/4 inch", inches: '< 1/4"' },
  "quarter-to-half": { label: "About 1/4 up to 1/2 inch", inches: '1/4"-1/2"' },
  "half-to-one": { label: "Over 1/2 and up to 1 inch", inches: '>1/2"-1"' },
  "over-one": { label: "More than 1 inch", inches: '> 1"' },
}

interface YearWindows {
  primary: { start: Date; end: Date }
  secondary: { start: Date; end: Date } | null
}

/**
 * Build the dethatching windows for a given calendar year. Warm-season grasses
 * dethatch during peak summer growth (late spring through summer, after full
 * green-up), so they get one primary window. Cool-season grasses recover best in
 * early fall, with early spring as an acceptable backup, so they get both.
 *
 * ⚠ Year-shift bug class: windows that have already ended this year must roll
 * forward to next year, or a late-fall user gets told the window is "350 days
 * away" while actually sitting a few weeks before next year's window. The caller
 * picks the relevant year per window (see below); this function only builds the
 * candidate dates for one year. Same lesson as the aeration + herbicide pickers.
 */
function buildWindowsForYear(
  isWarmSeason: boolean,
  lastFrost: Date | null,
  firstFrost: Date | null,
  yr: number
): YearWindows {
  if (isWarmSeason) {
    // Late spring THROUGH summer: warm-season grass heals dethatching damage
    // during active growth, which runs until roughly 7 weeks before first
    // frost (extension guidance). Anchoring the end to lastFrost capped the
    // window in April-May across the south and told Bermuda owners to skip
    // peak summer - the 2026-07 agronomic audit flagged that as a bug.
    // Start is lastFrost+42 (~6 weeks) so frost clearance alone doesn't open
    // the window while southern lawns are still dormant in February.
    const primaryStart = lastFrost ? addDays(lastFrost, 42) : new Date(yr, 4, 1) // ~May 1
    const primaryEnd = firstFrost ? addDays(firstFrost, -49) : new Date(yr, 8, 1) // ~Sep 1
    return { primary: { start: primaryStart, end: primaryEnd }, secondary: null }
  }

  // Cool-season: early fall is primary (grass rebounds into its fall growth
  // surge before winter), early spring is the acceptable backup. The fall
  // start is floored at Aug 10: early-frost regions (Denver, Minneapolis)
  // otherwise computed a late-JULY start, i.e. dethatching into summer heat
  // before fall growth resumes (same 2026-07 audit).
  const fallFloor = new Date(yr, 7, 10) // Aug 10
  const rawFallStart = firstFrost ? addDays(firstFrost, -56) : new Date(yr, 7, 15) // ~Aug 15
  const fallStart = rawFallStart < fallFloor ? fallFloor : rawFallStart
  const fallEnd = firstFrost ? addDays(firstFrost, -21) : new Date(yr, 9, 1) // ~Oct 1
  const springStart = lastFrost ? addDays(lastFrost, 7) : new Date(yr, 3, 1) // ~Apr 1
  const springEnd = lastFrost ? addDays(lastFrost, 35) : new Date(yr, 4, 15) // ~May 15
  return {
    primary: { start: fallStart, end: fallEnd },
    secondary: { start: springStart, end: springEnd },
  }
}

function daysUntil(date: Date, now: Date): number {
  return Math.ceil((date.getTime() - now.getTime()) / (24 * 60 * 60 * 1000))
}

// Window dates are date-only (midnight). Comparing a live timestamp against a
// midnight end would mark the whole displayed closing day as already-past and
// prematurely roll users to next year's window, so end bounds compare against
// the end of that calendar day.
function endOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)
}

function isWithin(date: Date, start: Date, end: Date): boolean {
  return date >= start && date <= endOfDay(end)
}

export function calculateDethatching(input: DethatchingInput): DethatchingResult {
  const { zipCode, thatchDepth } = input

  const context = buildCanonicalToolContext({
    toolId: "dethatching",
    grassType: input.grassType,
    lawnSize: input.lawnSize,
  })

  const grass = GRASS_CHARACTERISTICS[context.grassType]
  if (!grass) {
    throw new Error("Dethatching data is not available for this grass type.")
  }
  if (!THATCH_LABELS[thatchDepth]) {
    throw new Error("Please choose a thatch depth estimate.")
  }

  const region = getRegionFromZipCode(zipCode)
  if (region === "Unknown") {
    throw new Error("We could not determine your region from this ZIP code. Try a nearby ZIP.")
  }

  const climateData = REGIONAL_CLIMATE_DATA[region]
  if (!climateData) {
    throw new Error("Climate data is not available for your region.")
  }

  const isWarmSeason = grass.season === "Warm"
  const now = new Date()
  const thisYear = now.getFullYear()

  const currentMonth = now.getMonth()
  let currentSeason: "spring" | "summer" | "fall" | "winter"
  if (currentMonth >= 2 && currentMonth <= 4) currentSeason = "spring"
  else if (currentMonth >= 5 && currentMonth <= 7) currentSeason = "summer"
  else if (currentMonth >= 8 && currentMonth <= 10) currentSeason = "fall"
  else currentSeason = "winter"

  // Frost dates per year. `useLatest=false` for last frost (earliest safe date),
  // `useLatest=true` for first frost (latest, most conservative).
  const thisLastFrost = climateData.lastFrost ? parseFrostDate(climateData.lastFrost, thisYear, false) : null
  const thisFirstFrost = climateData.firstFrost ? parseFrostDate(climateData.firstFrost, thisYear, true) : null

  const thisYr = buildWindowsForYear(isWarmSeason, thisLastFrost, thisFirstFrost, thisYear)
  const nextLastFrost = climateData.lastFrost ? parseFrostDate(climateData.lastFrost, thisYear + 1, false) : null
  const nextFirstFrost = climateData.firstFrost ? parseFrostDate(climateData.firstFrost, thisYear + 1, true) : null
  const nextYr = buildWindowsForYear(isWarmSeason, nextLastFrost, nextFirstFrost, thisYear + 1)

  // Roll each window forward to next year once this year's has already ended.
  const usePrimaryThisYear = now <= endOfDay(thisYr.primary.end)
  const primaryDates = usePrimaryThisYear ? thisYr.primary : nextYr.primary

  let secondaryDates: { start: Date; end: Date } | null = null
  if (thisYr.secondary && nextYr.secondary) {
    const useSecondaryThisYear = now <= endOfDay(thisYr.secondary.end)
    secondaryDates = useSecondaryThisYear ? thisYr.secondary : nextYr.secondary
  }

  const primaryWindow: DethatchWindow = {
    start: primaryDates.start,
    end: primaryDates.end,
    isPrimary: true,
    description: isWarmSeason
      ? "Late spring through summer, while the lawn is fully green and growing hard"
      : "Early fall, so cool-season grass heals during its fall growth surge",
  }

  const secondaryWindow: DethatchWindow | null = secondaryDates
    ? {
        start: secondaryDates.start,
        end: secondaryDates.end,
        isPrimary: false,
        description: "Early spring after green-up is an acceptable backup window",
      }
    : null

  // Status + recommended window. Prefer whichever window the user is standing in,
  // otherwise the soonest upcoming one.
  const windows = [primaryWindow, secondaryWindow].filter((w): w is DethatchWindow => w !== null)

  let status: DethatchStatus = "wait"
  let recommendedWindow = primaryWindow
  let daysRemaining: number | undefined
  let timeUntilOptimal: string | null = null
  let timingAdvice = ""

  const inPrimary = isWithin(now, primaryWindow.start, primaryWindow.end)
  const inSecondary = secondaryWindow ? isWithin(now, secondaryWindow.start, secondaryWindow.end) : false

  if (inPrimary) {
    status = "optimal"
    recommendedWindow = primaryWindow
    daysRemaining = daysUntil(primaryWindow.end, now)
    timingAdvice = isWarmSeason
      ? "You are in the peak growth window right now. Warm-season grass recovers fastest when you dethatch during active summer growth."
      : "You are in the ideal fall window right now. Dethatch and the cool-season lawn will fill back in before winter."
  } else if (inSecondary && secondaryWindow) {
    status = "acceptable"
    recommendedWindow = secondaryWindow
    daysRemaining = daysUntil(secondaryWindow.end, now)
    timingAdvice =
      "Spring is a workable backup window. Fall is still the first choice, but light spring dethatching after green-up is fine."
  } else {
    // Not in a window: find the soonest upcoming window start.
    const upcoming = windows
      .filter((w) => w.start > now)
      .sort((a, b) => a.start.getTime() - b.start.getTime())[0]
    recommendedWindow = upcoming ?? primaryWindow
    const daysToStart = daysUntil(recommendedWindow.start, now)
    daysRemaining = daysToStart
    if (daysToStart <= 21 && daysToStart >= 0) {
      status = "approaching"
      timeUntilOptimal = `${daysToStart} days until your ${recommendedWindow.isPrimary ? "primary" : "backup"} window opens`
      timingAdvice = "Your dethatching window is almost here. Line up your equipment now so you can go the moment it opens."
    } else {
      status = currentSeason === "winter" || (isWarmSeason && currentSeason === "fall") ? "off-season" : "wait"
      timeUntilOptimal = `${daysToStart} days until the window opens`
      timingAdvice = isWarmSeason
        ? "Hold off. Dethatching a dormant or stressed warm-season lawn tears out crowns it cannot regrow until summer."
        : "Hold off. Dethatching in summer heat or deep winter stresses the lawn when it cannot recover. Wait for the window."
    }
  }

  // Verdict from thatch depth. The 1/2 inch line is the standard action threshold:
  // a thin thatch layer is healthy insulation, but past ~1/2 inch it blocks water,
  // air, and fertilizer from reaching the soil.
  let verdict: DethatchVerdict
  let verdictHeadline: string
  let verdictSummary: string
  let method: DethatchMethod
  let methodLabel: string
  let methodReason: string
  let passes: string

  if (thatchDepth === "under-quarter" || thatchDepth === "quarter-to-half") {
    verdict = "monitor"
    method = "none"
    methodLabel = "No dethatching needed"
    passes = "None this year"
    verdictHeadline = "Your thatch layer is healthy"
    verdictSummary =
      "A thatch layer under 1/2 inch is a good thing. It cushions the crowns, holds moisture, and moderates soil temperature. Leave it alone and keep it in check with regular core aeration instead of tearing it up."
    methodReason =
      "Below 1/2 inch there is nothing to remove. Core aeration once a year keeps thatch from ever building to a problem."
  } else if (thatchDepth === "half-to-one") {
    verdict = "dethatch"
    verdictHeadline = "Time to dethatch"
    verdictSummary =
      "At 1/2 to 1 inch, thatch is starting to shed water and block fertilizer. Plan a single dethatching pass in your window below, then follow with overseeding while the soil is exposed."
    if (isWarmSeason) {
      method = "power-rake"
      methodLabel = "Power rake (dethatcher)"
      methodReason =
        "Warm-season grass at this depth handles a power rake well during summer growth. Set the blades to just nick the soil surface, not gouge it."
    } else {
      method = "power-rake"
      methodLabel = "Power rake (dethatcher)"
      methodReason =
        "A power rake with spring tines pulls this layer out cleanly. Core aeration alone is gentler but will not clear a layer this thick on its own."
    }
    passes = "One pass in a single direction"
  } else {
    // over-one
    verdict = "dethatch-urgent"
    verdictHeadline = "Heavy thatch: dethatch aggressively"
    verdictSummary =
      "Over 1 inch of thatch is a genuine problem. Water pools on top, roots grow into the thatch instead of the soil, and disease sets in. This needs an aggressive vertical mower, likely in more than one pass, and probably again next season."
    method = "vertical-mower"
    methodLabel = "Vertical mower (verticutter)"
    methodReason = isWarmSeason
      ? "A vertical mower with fixed knife blades cuts deep enough to break up a heavy warm-season thatch layer. Do it at peak summer growth so the lawn can recover from the hard tear-up."
      : "A vertical mower cuts deeper than a spring-tine power rake. Removing more than an inch in one shot can shock the lawn, so split it across two lighter passes or two seasons."
    passes = "Two passes in a cross-hatch pattern, or split across two seasons"
  }

  // Equipment sizing from lawn size.
  const sqft = context.lawnSizeSqft
  let equipmentGuidance: string
  if (verdict === "monitor") {
    equipmentGuidance =
      "No machine needed. Save the rental fee and put down a core-aeration pass in the same window instead."
  } else if (sqft && sqft <= 3000) {
    // Guidance must agree with the method verdict: a heavy (>1") layer needs a
    // vertical mower's knife blades even on a small lawn; a rake or light
    // electric unit only handles the moderate band.
    equipmentGuidance =
      verdict === "dethatch-urgent"
        ? "Even on a lawn this size, a layer over an inch is beyond a manual rake or light electric unit. Rent a compact vertical mower (verticutter) for a half day; the small area means the rental goes quickly."
        : "For a lawn this size, an electric dethatcher or even a stiff manual dethatching rake is enough, and cheaper than a rental. A rake works for spot areas under a few hundred square feet."
  } else if (sqft && sqft <= 8000) {
    equipmentGuidance =
      "Rent a gas power rake or tow-behind dethatcher for the day. A manual rake is too much work at this size, and an electric unit will be slow."
  } else if (sqft && sqft > 8000) {
    equipmentGuidance =
      "Rent a self-propelled power rake or vertical mower, or hire it out. Hand raking or a small electric unit is not realistic at this size, and you will want the bagging attachment for cleanup."
  } else {
    equipmentGuidance =
      "Rent a gas power rake or vertical mower from a hardware store for the day. Add your lawn size in the form for a sizing call, and grab the bagging attachment for cleanup either way."
  }

  const steps: string[] = verdict === "monitor"
    ? [
        isWarmSeason
          ? "Mark your window below and set a reminder for a core-aeration pass during late-spring or summer growth."
          : "Mark your window below and set a reminder for a fall or spring core-aeration pass.",
        "Water the lawn a day or two before aerating so the tines pull full cores.",
        "Leave the thatch layer alone. It is doing its job at this depth.",
      ]
    : [
        "Mow the lawn to about half its normal height and bag the clippings before you start.",
        "Water lightly a day ahead so the soil is moist but not soggy, which lets the tines lift thatch cleanly.",
        `Make ${verdict === "dethatch-urgent" ? "your cross-hatch passes" : "one pass"} with the machine set to just reach the soil surface.`,
        "Rake up and haul off the loosened thatch. There will be more debris than you expect.",
        "Overseed and topdress right away while the soil is exposed, then keep it watered until the new grass is established.",
      ]

  const followUpActions: string[] = [
    "Overseed thin areas while the soil is exposed, then keep it moist for 2 to 3 weeks",
    "Apply a starter fertilizer to feed recovery and new seed",
    "Core-aerate in the same window if the soil is also compacted",
    "Water deeply after dethatching to help the lawn bounce back",
  ]

  const avoidActions: string[] = [
    isWarmSeason
      ? "Dethatching a dormant or brown warm-season lawn: it tears out crowns that cannot regrow until summer"
      : "Dethatching in summer heat or hard winter, when a cool-season lawn cannot recover from the damage",
    "Removing more than an inch of thatch in a single aggressive pass: split heavy jobs across passes or seasons",
    "Dethatching soggy soil, which pulls out live grass along with the thatch",
    "Skipping overseeding afterward, which leaves bare soil open to weeds",
  ]

  return attachToolEngineMetadata(
    {
      grassType: grass.name,
      zipCode,
      region,
      season: grass.season as "Warm" | "Cool",
      currentSeason,
      thatchDepth,
      thatchDepthLabel: THATCH_LABELS[thatchDepth].label,
      thatchInches: THATCH_LABELS[thatchDepth].inches,
      verdict,
      needsDethatching: verdict !== "monitor",
      verdictHeadline,
      verdictSummary,
      method,
      methodLabel,
      methodReason,
      passes,
      equipmentGuidance,
      primaryWindow,
      secondaryWindow,
      recommendedWindow,
      status,
      timingAdvice,
      timeUntilOptimal,
      daysRemaining,
      regionalData: {
        region,
        lastFrost: thisLastFrost,
        firstFrost: thisFirstFrost,
      },
      steps,
      followUpActions,
      avoidActions,
      lawnSizeSqft: sqft,
    },
    context
  )
}
